/**
 * useScrollReveal — trigger a one-shot reveal animation when an element
 * scrolls into view.
 *
 * Returns a ref you attach to the element you want to animate, plus a
 * `visible` boolean that flips true the first time the element crosses
 * the configured intersection threshold. Consumers style around it with
 * their own CSS transitions.
 *
 * Respects `prefers-reduced-motion` — users who opt out of animation get
 * `visible = true` immediately so nothing fades in and content is fully
 * visible from the first paint.
 */

import { useEffect, useRef, useState } from 'react';

export interface UseScrollRevealOptions {
  /** Fraction of the element that must be visible to trigger. Default 0.15. */
  threshold?: number;
  /** rootMargin passed to IntersectionObserver. Default '0px'. */
  rootMargin?: string;
  /** If true, resets to false whenever the element leaves the viewport. Default false (one-shot). */
  repeat?: boolean;
}

export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollRevealOptions = {},
): [React.RefObject<T | null>, boolean] {
  const { threshold = 0.15, rootMargin = '0px', repeat = false } = options;
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Users who prefer reduced motion just get the visible state
    // immediately — no animation, no observer overhead.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    // Older browsers without IntersectionObserver: same fallback.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (!repeat) observer.unobserve(entry.target);
        } else if (repeat) {
          setVisible(false);
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, repeat]);

  return [ref, visible];
}
